import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Sparkles, Target, Package } from 'lucide-react';
import { getCatalog, searchProducts, getQuizRecommendations, getClient, getKioskProduct } from '../api/client';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/Button';
import type { Product, Client, QuizAnswers } from '../types';
import styles from './Kiosk.module.css';

type TabType = 'catalog' | 'search' | 'quiz';

const QUIZ_QUESTIONS = [
    {
        question: 'Untuk siapa parfum ini?',
        subtitle: 'Pilih gender target',
        key: 'gender' as const,
        options: [
            { icon: '👨', text: 'Pria', value: 'Pria' },
            { icon: '👩', text: 'Wanita', value: 'Wanita' },
            { icon: '🧑', text: 'Unisex', value: 'Unisex' },
        ],
    },
    {
        question: 'Untuk kesempatan apa?',
        subtitle: 'Kapan parfum ini akan dipakai',
        key: 'occasion' as const,
        options: [
            { icon: '💼', text: 'Kerja/Kantor', value: 'Office' },
            { icon: '🎉', text: 'Pesta/Hangout', value: 'Party' },
            { icon: '💑', text: 'Kencan', value: 'Date' },
            { icon: '🏠', text: 'Sehari-hari', value: 'Daily' },
            { icon: '🌙', text: 'Malam Hari', value: 'Evening' },
        ],
    },
    {
        question: 'Karakter wangi yang disukai?',
        subtitle: 'Pilih keluarga aroma favorit',
        key: 'scent_family' as const,
        options: [
            { icon: '🌸', text: 'Floral/Bunga', value: 'Floral' },
            { icon: '🌲', text: 'Woody/Kayu', value: 'Woody' },
            { icon: '🍋', text: 'Fresh/Segar', value: 'Fresh' },
            { icon: '🪵', text: 'Oriental/Rempah', value: 'Oriental' },
            { icon: '🍫', text: 'Sweet/Manis', value: 'Gourmand' },
        ],
    },
    {
        question: 'Seberapa kuat wanginya?',
        subtitle: 'Intensitas aroma yang diinginkan',
        key: 'intensity' as const,
        options: [
            { icon: '🌬️', text: 'Lembut', value: 'Intimate' },
            { icon: '💨', text: 'Sedang', value: 'Moderate' },
            { icon: '💪', text: 'Kuat', value: 'Strong' },
        ],
    },
    {
        question: 'Kesan apa yang ingin ditampilkan?',
        subtitle: 'Personality yang ingin dipancarkan',
        key: 'personality' as const,
        options: [
            { icon: '✨', text: 'Elegan', value: 'Elegant' },
            { icon: '🔥', text: 'Bold/Berani', value: 'Bold' },
            { icon: '💕', text: 'Romantis', value: 'Romantic' },
            { icon: '⚡', text: 'Energik', value: 'Sporty' },
            { icon: '🎩', text: 'Sophisticated', value: 'Sophisticated' },
        ],
    },
];

export function KioskPage() {
    const { clientId } = useParams<{ clientId: string }>();
    const [activeTab, setActiveTab] = useState<TabType>('catalog');
    const [client, setClient] = useState<Client | null>(null);

    // Catalog state
    const [products, setProducts] = useState<Product[]>([]);
    const [catalogSearch, setCatalogSearch] = useState('');
    const [loading, setLoading] = useState(false);

    // AI Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Product[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // Quiz state
    const [quizStep, setQuizStep] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState<QuizAnswers>({});
    const [quizResults, setQuizResults] = useState<Product[]>([]);
    const [quizLoading, setQuizLoading] = useState(false);
    const [showQuizResults, setShowQuizResults] = useState(false);

    // Modal state
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    useEffect(() => {
        if (clientId) {
            loadClient();
            loadCatalog();
        }
    }, [clientId]);

    const loadClient = async () => {
        if (!clientId) return;
        try {
            const data = await getClient(clientId);
            setClient(data);
        } catch (err) {
            console.error('Failed to load client:', err);
        }
    };

    const loadCatalog = async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const data = await getCatalog(clientId, 1, 50, catalogSearch);
            setProducts(data.products);
        } catch (err) {
            console.error('Failed to load catalog:', err);
        }
        setLoading(false);
    };

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (activeTab === 'catalog') loadCatalog();
        }, 300);
        return () => clearTimeout(debounce);
    }, [catalogSearch]);

    const handleSearch = async () => {
        if (!clientId || !searchQuery.trim()) return;
        setSearchLoading(true);
        try {
            const data = await searchProducts(clientId, searchQuery, 12);
            setSearchResults(data.products);
        } catch (err) {
            console.error('Search failed:', err);
        }
        setSearchLoading(false);
    };

    const handleQuizSubmit = async () => {
        if (!clientId) return;
        setQuizLoading(true);
        try {
            const data = await getQuizRecommendations(clientId, quizAnswers);
            setQuizResults(data.recommendations);
            setShowQuizResults(true);
        } catch (err) {
            console.error('Quiz failed:', err);
        }
        setQuizLoading(false);
    };

    const restartQuiz = () => {
        setQuizStep(0);
        setQuizAnswers({});
        setQuizResults([]);
        setShowQuizResults(false);
    };

    const handleProductClick = async (product: Product) => {
        if (!clientId) return;
        try {
            const data = await getKioskProduct(clientId, product.id);
            setSelectedProduct(data);
        } catch (err) {
            setSelectedProduct(product);
        }
    };

    return (
        <div className={styles.container}>
            {/* Hero */}
            <section className={styles.hero}>
                <h1 className={styles.logo}>ScentMatch</h1>
                <p className={styles.clientName}>{client?.client_name || clientId}</p>
                <h2 className={styles.title}>
                    Temukan Parfum<br />Impianmu ✨
                </h2>
                <p className={styles.subtitle}>
                    Cari berdasarkan karakter, mood, atau ikuti quiz untuk rekomendasi personal
                </p>
            </section>

            {/* Tabs */}
            <nav className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'catalog' ? styles.active : ''}`}
                    onClick={() => setActiveTab('catalog')}
                >
                    <Package size={18} /> Katalog
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'search' ? styles.active : ''}`}
                    onClick={() => setActiveTab('search')}
                >
                    <Sparkles size={18} /> AI Search
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'quiz' ? styles.active : ''}`}
                    onClick={() => setActiveTab('quiz')}
                >
                    <Target size={18} /> Quiz
                </button>
            </nav>

            {/* Content */}
            <main className={styles.main}>
                {/* Catalog Tab */}
                {activeTab === 'catalog' && (
                    <div>
                        <div className={styles.searchBox}>
                            <Search className={styles.searchIcon} size={20} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Cari nama parfum atau brand..."
                                value={catalogSearch}
                                onChange={(e) => setCatalogSearch(e.target.value)}
                            />
                        </div>

                        {loading ? (
                            <div className={styles.loading}>
                                <div className="spinner" />
                                <p>Memuat katalog...</p>
                            </div>
                        ) : products.length === 0 ? (
                            <div className={styles.empty}>
                                <Package size={48} />
                                <h3>Belum Ada Produk</h3>
                                <p>Katalog sedang dipersiapkan</p>
                            </div>
                        ) : (
                            <div className={styles.grid}>
                                {products.map((product) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        onClick={() => handleProductClick(product)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* AI Search Tab */}
                {activeTab === 'search' && (
                    <div>
                        <div className={styles.searchBox}>
                            <Sparkles className={styles.searchIcon} size={20} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Cari dengan deskripsi... contoh: parfum segar untuk pria ke kantor"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <Button onClick={handleSearch} loading={searchLoading} size="sm">
                                Cari
                            </Button>
                        </div>
                        <p className={styles.searchHint}>
                            Ketik apapun! AI akan mencari parfum yang paling cocok berdasarkan deskripsimu
                        </p>

                        {searchLoading ? (
                            <div className={styles.loading}>
                                <div className="spinner" />
                                <p>AI sedang mencari parfum terbaik...</p>
                            </div>
                        ) : searchResults.length > 0 ? (
                            <div className={styles.grid}>
                                {searchResults.map((product) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        showScore
                                        onClick={() => handleProductClick(product)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className={styles.empty}>
                                <Search size={48} />
                                <h3>Mulai Pencarian</h3>
                                <p>Ketik deskripsi parfum impianmu di kolom pencarian</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Quiz Tab */}
                {activeTab === 'quiz' && (
                    <div>
                        {!showQuizResults ? (
                            <div className={styles.quizCard}>
                                {/* Progress */}
                                <div className={styles.quizProgress}>
                                    {QUIZ_QUESTIONS.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`${styles.progressDot} ${i < quizStep ? styles.completed : ''} ${i === quizStep ? styles.active : ''}`}
                                        />
                                    ))}
                                </div>

                                {/* Question */}
                                <h2 className={styles.quizQuestion}>
                                    {QUIZ_QUESTIONS[quizStep].question}
                                </h2>
                                <p className={styles.quizSubtitle}>
                                    {QUIZ_QUESTIONS[quizStep].subtitle}
                                </p>

                                {/* Options */}
                                <div className={styles.quizOptions}>
                                    {QUIZ_QUESTIONS[quizStep].options.map((opt) => (
                                        <div
                                            key={opt.value}
                                            className={`${styles.quizOption} ${quizAnswers[QUIZ_QUESTIONS[quizStep].key] === opt.value ? styles.selected : ''}`}
                                            onClick={() => setQuizAnswers({ ...quizAnswers, [QUIZ_QUESTIONS[quizStep].key]: opt.value })}
                                        >
                                            <span className={styles.optionIcon}>{opt.icon}</span>
                                            <span>{opt.text}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Buttons */}
                                <div className={styles.quizButtons}>
                                    {quizStep > 0 && (
                                        <Button variant="outline" onClick={() => setQuizStep(quizStep - 1)}>
                                            ← Kembali
                                        </Button>
                                    )}
                                    {quizStep < QUIZ_QUESTIONS.length - 1 ? (
                                        <Button
                                            onClick={() => {
                                                if (quizAnswers[QUIZ_QUESTIONS[quizStep].key]) {
                                                    setQuizStep(quizStep + 1);
                                                }
                                            }}
                                            disabled={!quizAnswers[QUIZ_QUESTIONS[quizStep].key]}
                                        >
                                            Lanjut →
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleQuizSubmit}
                                            loading={quizLoading}
                                            disabled={!quizAnswers[QUIZ_QUESTIONS[quizStep].key]}
                                        >
                                            Lihat Hasil 🎉
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className={styles.resultsHeader}>
                                    <h2>🎉 Parfum Rekomendasimu!</h2>
                                    <p>Berdasarkan preferensimu, ini parfum yang paling cocok</p>
                                </div>

                                {quizResults.length > 0 ? (
                                    <div className={styles.grid}>
                                        {quizResults.map((product) => (
                                            <ProductCard
                                                key={product.id}
                                                product={product}
                                                showScore
                                                onClick={() => handleProductClick(product)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className={styles.empty}>
                                        <Target size={48} />
                                        <h3>Belum Ada Rekomendasi</h3>
                                        <p>Coba lagi nanti</p>
                                    </div>
                                )}

                                <div className={styles.resultsFooter}>
                                    <Button variant="outline" onClick={restartQuiz}>
                                        🔄 Ulangi Quiz
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Product Modal */}
            {selectedProduct && (
                <div className={styles.modalOverlay} onClick={() => setSelectedProduct(null)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.modalClose} onClick={() => setSelectedProduct(null)}>
                            ×
                        </button>
                        <div className={styles.modalContent}>
                            <img
                                src={selectedProduct.image_url || `https://via.placeholder.com/300x300/1a1a2e/8b5cf6?text=${encodeURIComponent(selectedProduct.brand)}`}
                                alt={selectedProduct.parfum_name}
                                className={styles.modalImage}
                            />
                            <div className={styles.modalInfo}>
                                <p className={styles.modalBrand}>{selectedProduct.brand}</p>
                                <h2 className={styles.modalName}>{selectedProduct.parfum_name}</h2>

                                {selectedProduct.notes && (
                                    <div className={styles.modalSection}>
                                        <h4>Notes</h4>
                                        <p>{selectedProduct.notes}</p>
                                    </div>
                                )}

                                <div className={styles.modalTags}>
                                    {selectedProduct.gender && <span className={styles.tag}>{selectedProduct.gender}</span>}
                                    {selectedProduct.category && <span className={styles.tag}>{selectedProduct.category}</span>}
                                    {selectedProduct.season && <span className={styles.tag}>{selectedProduct.season}</span>}
                                </div>

                                {selectedProduct.personality_tags && (
                                    <div className={styles.modalSection}>
                                        <h4>Personality</h4>
                                        <p>{selectedProduct.personality_tags}</p>
                                    </div>
                                )}

                                {selectedProduct.similar_to && (
                                    <div className={styles.modalSection}>
                                        <h4>Similar To</h4>
                                        <p>{selectedProduct.similar_to}</p>
                                    </div>
                                )}

                                {selectedProduct.price && (
                                    <p className={styles.modalPrice}>
                                        Rp {Number(selectedProduct.price).toLocaleString('id-ID')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
